import { useEffect } from 'react'
import type { Conn } from '../live'
import type { UserView } from '../api'

/*
 * The Strevix logo of the cabinet and the console (design/brand, BR1 S1 "Core · Orbit"),
 * as a state logo (BR1 report, 16.10 (4) and (5)):
 *   on air      — the stream is on air (the sender decides: traffic from OBS): the loop
 *   connecting  — live data is connecting or reconnecting
 *   waiting     — Studio / Status before the stream: waiting for the signal from OBS
 *   static      — everything else (admin pages, apps, no stream)
 * The files are web/public/brand/ (copies of design/brand/svg/final); the animation is CSS
 * inside the SVG and stops under prefers-reduced-motion by itself. The state still reads
 * from the badges of the page: the logo repeats it, it does not replace it.
 */

export type BrandState = 'static' | 'onair' | 'connecting' | 'waiting'

const FILE: Record<BrandState, string> = { static: '', onair: '-loop', connecting: '-connecting', waiting: '-waiting' }
const LABEL: Record<BrandState, string> = { static: 'Strevix', onair: 'Strevix — on air', connecting: 'Strevix — connecting', waiting: 'Strevix — waiting for the stream' }

/** dark | light: the theme is set once at start (main.tsx, ?theme=light). */
const theme = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark')

export function brandState({ view, conn, liveScreen }: { view: UserView | null; conn: Conn; liveScreen: boolean }): BrandState {
  if (view && view.node !== 'unreachable' && view.inputs.some((i) => i.live)) return 'onair'
  if (!view) return 'static'
  if (conn !== 'open') return 'connecting'
  return liveScreen ? 'waiting' : 'static'
}

/** Header logo: the full logo from 640 px, the mark alone on a phone (the header buttons fit one row). */
export function BrandLogo({ state }: { state: BrandState }) {
  const th = theme()
  const f = FILE[state]
  return (
    <span className="flex items-center" data-testid="brand-logo" data-state={state} role="img" aria-label={LABEL[state]}>
      <img src={`/brand/horizontal${f}-${th}.svg`} alt="" width={86} height={28} className="block h-7 w-auto max-sm:hidden" draggable={false} />
      <img src={`/brand/mark${f}-${th}.svg`} alt="" width={26} height={28} className="block h-7 w-auto sm:hidden" draggable={false} />
    </span>
  )
}

/** The tab icon: On Air while the stream is on air, the plain one otherwise. */
export function useBrandFavicon(state: BrandState) {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) return
    link.href = state === 'onair' ? '/brand/favicon-onair.svg' : '/favicon.svg'
  }, [state])
}
